import {
  BILLING_MODE_LABELS,
  BUYABLE_ADDONS,
  SUBSCRIPTION_OFFER_LABELS,
  chargedAddons,
  offerLabel,
  type BillingMode,
  type ModuleKey,
  COUNTRIES,
  COMMISSION_STATUS_LABELS,
  OFFER_LABELS,
  OFFERS,
  PROSPECT_STATUS_LABELS,
  PROVINCES,
  type CommissionStatus,
  type Offer,
  type ProspectDTO,
  type ProspectStatus,
  type SellerDTO,
} from '@bifeai/shared';
import { Badge, Button, Callout, Dialog, Field, Input, Select, Textarea } from '@bifeai/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ApiError, errorMessage } from '../lib/api';

const ADDON_LABELS: Record<string, string> = {
  CUTTING: 'Desposte automático + inteligencia',
  ATTENDANCE: 'Asistencia del personal',
  ORDERS_CHAT: 'bifi (asistente de ventas por WhatsApp)',
};

export function ProspectStatusBadge({ status }: { status: ProspectStatus }) {
  const tone = status === 'WON' ? 'success' : status === 'LOST' ? 'neutral' : status === 'LINK_SENT' ? 'warning' : 'info';
  return (
    <Badge tone={tone} dot>
      {PROSPECT_STATUS_LABELS[status]}
    </Badge>
  );
}

export function CommissionStatusBadge({ status }: { status: CommissionStatus }) {
  const tone = status === 'PAID' ? 'success' : status === 'CANCELED' ? 'neutral' : 'warning';
  return <Badge tone={tone}>{COMMISSION_STATUS_LABELS[status]}</Badge>;
}

/** Desde / Hasta (vacío = todo). */
export function useRange() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const qs = (extra: Record<string, string | undefined> = {}, pageSize = 20) => {
    const p = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
    return p.toString();
  };
  return { from, to, page, setPage, setFrom: (v: string) => (setFrom(v), setPage(1)), setTo: (v: string) => (setTo(v), setPage(1)), qs };
}

export function RangeFilter({ r, children }: { r: ReturnType<typeof useRange>; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {children}
      <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Desde
        <Input type="date" value={r.from} onChange={(e) => r.setFrom(e.target.value)} style={{ width: 160 }} />
      </label>
      <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        Hasta
        <Input type="date" value={r.to} onChange={(e) => r.setTo(e.target.value)} style={{ width: 160 }} />
      </label>
      {(r.from || r.to) && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            r.setFrom('');
            r.setTo('');
          }}
        >
          Todo
        </Button>
      )}
    </div>
  );
}

export function Pager({ page, total, pageSize, onPage }: { page: number; total: number; pageSize: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16 }}>
      <Button size="sm" icon={<ChevronLeft />} disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Anterior
      </Button>
      <span className="bf-cell-sub">
        Página {page} de {pages}
      </span>
      <Button size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Siguiente <ChevronRight size={16} aria-hidden />
      </Button>
    </div>
  );
}

export const fieldsOf = (err: unknown): Record<string, string> => (err instanceof ApiError && err.fields ? err.fields : {});

/** Qué compra el prospecto: "Licencia Multisucursal · 3 locales + 1 adicional". */
export function prospectWhat(
  p: Pick<ProspectDTO, 'offer' | 'branches' | 'franchises' | 'addons'> & { bifiLines?: number; mode?: BillingMode },
): string {
  const n = p.bifiLines ?? 1;
  const lines = ` · ${n} ${n === 1 ? 'número' : 'números'} de WhatsApp`;
  if (p.offer === 'BIFI') return `${OFFER_LABELS.BIFI}${lines}`;
  return `${offerLabel(p.offer, p.mode)}${p.offer !== 'CARNICERIA' ? ` · ${p.branches} locales` : ''}${p.franchises ? ` · ${p.franchises} franquicias` : ''}${
    p.addons.length ? ` + ${p.addons.map((a) => ADDON_LABELS[a] ?? a).join(', ')}` : ''
  }${p.addons.includes('ORDERS_CHAT') ? lines : ''}`;
}

/** Cargar o editar un prospecto (el vendedor desde su portal o el admin a nombre de un vendedor). */
export function ProspectDialog({
  initial,
  sellers,
  onClose,
  onSave,
}: {
  initial?: ProspectDTO;
  /** Solo el admin: elige a qué vendedor se le carga. */
  sellers?: SellerDTO[];
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => Promise<unknown>;
}) {
  const [f, setF] = useState({
    sellerId: initial?.sellerId ?? '',
    business: initial?.business ?? '',
    contactName: initial?.contactName ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    cuit: initial?.cuit ?? '',
    country: initial?.country ?? 'AR',
    province: initial?.province ?? '',
    locality: initial?.locality ?? '',
    offer: (initial?.offer ?? 'CARNICERIA') as Offer,
    mode: (initial?.mode ?? 'LICENSE') as BillingMode,
    branches: initial?.branches ?? 1,
    franchises: initial?.franchises ?? 0,
    addons: initial?.addons ?? [],
    bifiLines: initial?.bifiLines ?? 1,
    notes: initial?.notes ?? '',
  });
  const withBifi = f.offer === 'BIFI' || f.addons.includes('ORDERS_CHAT');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      await onSave({
        ...f,
        // Admin: vacío = lo trabaja BifeAI directo (él mismo).
        sellerId: sellers ? f.sellerId || null : undefined,
        branches: f.offer === 'CARNICERIA' ? 1 : Math.max(f.offer === 'MULTISUCURSAL' ? 2 : 1, Number(f.branches) || 1),
        franchises: f.offer === 'FRANQUICIAS' ? Number(f.franchises) || 0 : 0,
        addons: f.offer === 'BIFI' ? [] : chargedAddons(f.offer, f.mode, f.addons as ModuleKey[]),
        mode: f.offer === 'BIFI' ? 'LICENSE' : f.mode,
        bifiLines: withBifi ? Math.max(1, Math.min(500, Math.floor(Number(f.bifiLines) || 1))) : 1,
      });
    } catch (err) {
      setErrors(fieldsOf(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={initial ? 'Editar prospecto' : 'Cargar prospecto'} wide>
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        {!initial && !sellers && (
          <Callout>
            Para cargarlo rápido alcanza con la carnicería, el contacto y el WhatsApp. BifeAI le arma el link de pago; cuando paga, se crea su cuenta
            y la venta queda a tu nombre.
          </Callout>
        )}
        {sellers && (
          <Field label="A cargo de" hint="Lo podés trabajar vos o pasárselo a un vendedor cuando quieras." error={errors.sellerId}>
            <Select value={f.sellerId} onChange={(e) => set('sellerId', e.target.value)}>
              <option value="">Yo (BifeAI directo)</option>
              {sellers
                .filter((s) => s.isActive || s.id === f.sellerId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
            </Select>
          </Field>
        )}
        <div className="bf-form-row">
          <Field label="Carnicería o marca" error={errors.business}>
            <Input value={f.business} onChange={(e) => set('business', e.target.value)} maxLength={120} autoFocus />
          </Field>
          <Field label="Contacto" error={errors.contactName}>
            <Input value={f.contactName} onChange={(e) => set('contactName', e.target.value)} maxLength={120} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Email" hint="Opcional ahora: se pide para el link de pago (con este entra a BifeAI)" error={errors.email}>
            <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} maxLength={160} />
          </Field>
          <Field label="WhatsApp" error={errors.phone}>
            <Input value={f.phone} onChange={(e) => set('phone', e.target.value)} maxLength={40} />
          </Field>
          <Field label="CUIT (opcional)" error={errors.cuit}>
            <Input value={f.cuit} onChange={(e) => set('cuit', e.target.value)} maxLength={13} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="País">
            <Select value={f.country} onChange={(e) => set('country', e.target.value)}>
              {COUNTRIES.map(([c, n]) => (
                <option key={c} value={c}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Provincia o región">
            {f.country === 'AR' ? (
              <Select value={f.province} onChange={(e) => set('province', e.target.value)}>
                <option value="">Elegí…</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Select>
            ) : (
              <Input value={f.province} onChange={(e) => set('province', e.target.value)} maxLength={60} />
            )}
          </Field>
          <Field label="Localidad">
            <Input value={f.locality} onChange={(e) => set('locality', e.target.value)} maxLength={80} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Qué quiere comprar" error={errors.offer}>
            <Select value={f.offer} onChange={(e) => set('offer', e.target.value as Offer)}>
              {OFFERS.map((o) => (
                <option key={o} value={o}>
                  {o === 'BIFI' ? OFFER_LABELS[o] : SUBSCRIPTION_OFFER_LABELS[o].replace('Abono ', '')}
                </option>
              ))}
            </Select>
          </Field>
          {f.offer !== 'BIFI' && (
            <Field
              label="Cómo paga"
              error={errors.mode}
              hint={
                f.mode === 'SUBSCRIPTION' ? 'Por mes, sin pago inicial. Solo con el link de pago (tarjeta).' : 'Pago único + mantenimiento anual.'
              }
            >
              <Select value={f.mode} onChange={(e) => set('mode', e.target.value as BillingMode)}>
                <option value="LICENSE">{BILLING_MODE_LABELS.LICENSE} de por vida</option>
                <option value="SUBSCRIPTION">{BILLING_MODE_LABELS.SUBSCRIPTION}</option>
              </Select>
            </Field>
          )}
          {f.offer !== 'CARNICERIA' && f.offer !== 'BIFI' && (
            <Field label="Locales propios (con la central)" error={errors.branches}>
              <Input
                type="number"
                min={f.offer === 'MULTISUCURSAL' ? 2 : 1}
                value={f.branches}
                onChange={(e) => set('branches', Number(e.target.value))}
              />
            </Field>
          )}
          {f.offer === 'FRANQUICIAS' && (
            <Field label="Franquicias" error={errors.franchises}>
              <Input type="number" min={0} value={f.franchises} onChange={(e) => set('franchises', Number(e.target.value))} />
            </Field>
          )}
        </div>
        {f.offer !== 'BIFI' && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {BUYABLE_ADDONS.filter((a) => chargedAddons(f.offer, f.mode, [a]).length > 0).map((a) => (
              <label key={a} className="bf-check" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={f.addons.includes(a)}
                  onChange={(e) => set('addons', e.target.checked ? [...f.addons, a] : f.addons.filter((x) => x !== a))}
                />
                {ADDON_LABELS[a]}
              </label>
            ))}
          </div>
        )}
        {withBifi && (
          <Field
            label="Números de WhatsApp"
            error={errors.bifiLines}
            hint="Cada número paga su abono mensual. Con más de 10 números, todos pasan al precio por volumen."
          >
            <Input
              type="number"
              min={1}
              max={500}
              value={f.bifiLines}
              onChange={(e) => set('bifiLines', Number(e.target.value))}
              style={{ maxWidth: 140 }}
            />
          </Field>
        )}
        <Field label="Notas">
          <Textarea value={f.notes} onChange={(e) => set('notes', e.target.value)} maxLength={2000} rows={3} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            {initial ? 'Guardar' : 'Cargar prospecto'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Marcar un prospecto como perdido, con el motivo. */
export function LostDialog({ p, onClose, onSave }: { p: ProspectDTO; onClose: () => void; onSave: (reason: string) => Promise<unknown> }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Perdido: ${p.business}`}>
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="¿Por qué se perdió?">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} autoFocus />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="danger"
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              try {
                await onSave(reason.trim());
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Marcar perdido
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Copia al portapapeles con aviso. */
export async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Montos como los escribe la gente: "1.250.000,50", "1250000.50" o "1.250.000". */
export function parseAmount(v: string): number {
  const t = v.trim().replace(/\s/g, '');
  if (!t) return 0;
  if (t.includes(',')) return Number(t.replace(/\./g, '').replace(',', '.')) || 0;
  if ((t.match(/\./g) ?? []).length > 1) return Number(t.replace(/\./g, '')) || 0;
  return Number(t) || 0;
}
